import { roundDecimal } from '@shared/utils/math';
import { CargoPlaceType } from '@shared/constants/cargo-places';
import _ from 'lodash';
import { CargoPlace } from '../../types/cargo-place';
import { PreOrder, PreOrderCargoPlaceDetails } from '../../types/pre-order';
import { CargoOrder, OrderCargoPlaceDetails } from '../../types/cargo';

export function getLoadingPercentAsUSPallet(
  placeQuantity: number,
  trailerLength: number,
) {
  const runningMeters = Math.ceil(placeQuantity / 2) * 1.2;
  return roundDecimal((runningMeters / trailerLength) * 100);
}

export function getLoadingPercent(
  runningMeters: number,
  trailerLength: number,
) {
  return roundDecimal((runningMeters / trailerLength) * 100);
}

export function getRunningMetersFromUSPallets(placeQuantity: number) {
  return roundDecimal((placeQuantity / 2) * 1.2);
}

export function numberOfPalletsThatCanBeLoaded(runningMeters: number) {
  return roundDecimal((runningMeters / 1.2) * 2);
}

const runningMetersByCartQuantity = new Map<number, number>([
  [1, 0.57],
  [2, 1.35],
  [3, 1.35],
  [4, 1.35],
  [5, 1.92],
  [6, 1.92],
  [7, 2.48],
  [8, 2.48],
  [9, 2.83],
  [13, 4.05],
]);

export function getRunningMetersFromCartQuantity(cartQuantity: number): number {
  if (cartQuantity <= 0) {
    return 0;
  }

  const sortedKeys = Array.from(runningMetersByCartQuantity.keys()).sort(
    (a, b) => b - a,
  );

  const bestMatch = sortedKeys.find(key => key <= cartQuantity);

  if (!bestMatch) {
    return runningMetersByCartQuantity.get(1)!;
  } // Если ничего не нашли, берем значение для 1 ящика

  const remainingQuantity = cartQuantity - bestMatch;

  const additionalMeters =
    remainingQuantity > 0 && remainingQuantity < 1
      ? runningMetersByCartQuantity.get(1)!
      : 0;

  return (
    runningMetersByCartQuantity.get(bestMatch)! +
    additionalMeters +
    getRunningMetersFromCartQuantity(Math.floor(remainingQuantity))
  );
}

export function countPalletsCartsAndBulks(
  items: Array<{
    cargoPlaceJson: CargoPlace;
    cargoPlaceQuantity: Nullable<number>;
  }>,
) {
  return items.reduce(
    ([pallets, carts, bulks], current) => {
      const cart = current.cargoPlaceJson.code === CargoPlaceType.CART;
      const bulk = current.cargoPlaceJson.code === CargoPlaceType.BULK;
      const pallet = ![CargoPlaceType.BULK, CargoPlaceType.CART].includes(
        current.cargoPlaceJson.code as CargoPlaceType,
      );
      const quantity = current.cargoPlaceQuantity ?? 0;
      return [
        pallet ? pallets + quantity : pallets,
        cart ? carts + quantity : carts,
        bulk ? bulks + quantity : bulks,
      ];
    },
    [0, 0, 0],
  );
}
export function getCartQuantityFromRunningMeters(meters: number): number {
  if (meters <= 0) {
    return 0;
  }

  const sortedEntries = Array.from(runningMetersByCartQuantity.entries()).sort(
    ([, a], [, b]) => b - a,
  );

  let cartCount = 0;

  for (const [quantity, length] of sortedEntries) {
    while (meters >= length) {
      meters -= length;
      cartCount += quantity;
    }
  }

  return cartCount;
}

export function getCalculatedQuantity(
  packageQuantityLimit: Nullable<number>,
  packageQuantity: Nullable<number>,
  nullable = true,
): Nullable<number> {
  const defaultValue = nullable ? null : 0;
  return packageQuantityLimit && packageQuantity
    ? roundDecimal(packageQuantity / Number(packageQuantityLimit)) ||
        defaultValue
    : defaultValue;
}

function joinEntries(
  data: Record<string, unknown>,
  keyValueSeparator = ':',
  entrySeparator = ',',
) {
  return Object.entries(data).reduce(
    (previousValue, [k, v], currentIndex, array) => {
      const separator = currentIndex === array.length - 1 ? '' : entrySeparator;
      return previousValue.concat(`${k}${keyValueSeparator}${v}${separator}`);
    },
    '',
  );
}

export function countBoxAndPlacesWithTypes(order: PreOrder | CargoOrder): {
  places: string;
  packageQuantity: string;
} {
  const data = getTotalPlacesAndBoxes(order.cargoPlacesJson);

  return {
    packageQuantity: joinEntries(data.packageQuantity, ' - '),
    places: joinEntries(data.places, ' - '),
  };
}

export function countBoxAndPlaces(order: PreOrder | CargoOrder): {
  places: string;
  packages: string;
} {
  const data = getTotalPlacesAndBoxes(order.cargoPlacesJson);
  return {
    packages: joinEntries(data.packageQuantity, ' ', ','),
    places: joinEntries(data.places, ' ', ','),
  };
}

function sumByGroup<T>(
  arr: T[],
  key: (x: T) => string,
  value: (x: T) => number,
) {
  return _.mapValues(_.groupBy(arr, key), group => _.sumBy(group, value));
}

export function getTotalPlacesAndBoxes(
  items: Array<PreOrderCargoPlaceDetails | OrderCargoPlaceDetails>,
) {
  return {
    places: sumByGroup(
      items,
      i => i.cargoPlaceJson?.name ?? '(без типа)',
      i => i.cargoPlaceQuantity ?? 0,
    ),
    packageQuantity: sumByGroup(
      items,
      i => i.packageTypeJson?.name ?? '(без типа)',
      i => i.packageQuantity ?? 0,
    ),
  };
}
export function countPlacesAndSumPackages(order: PreOrder | CargoOrder): {
  places: string;
  packages: number;
} {
  const data = getTotalPlacesAndBoxes(order.cargoPlacesJson);
  const packages = Object.values(data.packageQuantity).reduce(
    (previousValue, currentValue) => {
      return previousValue + currentValue;
    },
    0,
  );
  return {
    packages,
    places: joinEntries(data.places, ' ', ','),
  };
}
export function getComputedProperties(items: PreOrder[] | CargoOrder[]) {
  const groupedByCold = _.groupBy<PreOrder | CargoOrder>(
    items,
    (item: PreOrder | CargoOrder) => {
      return !!item.cold;
    },
  );

  const coldPlaces: PreOrderCargoPlaceDetails[] = [];
  const warmPlaces: PreOrderCargoPlaceDetails[] = [];

  Object.entries(groupedByCold).forEach(([cold, preOrders]) => {
    const target = cold === 'true' ? coldPlaces : warmPlaces;
    preOrders.forEach(p => {
      target.push(...p.cargoPlacesJson);
    });
  });

  const coldResult = getTotalPlacesAndBoxes(coldPlaces);
  const warmResult = getTotalPlacesAndBoxes(warmPlaces);

  const totalBox = [
    ...Object.values(coldResult.packageQuantity),
    ...Object.values(warmResult.packageQuantity),
  ].reduce((previousValue, currentValue) => {
    return previousValue + currentValue;
  }, 0);
  return {
    totalBox,
    totalColdPlaces: Object.entries(coldResult.places)
      .map(([place, quantity]) => `${roundDecimal(quantity)} ${place}`)
      .join(' / '),
    totalWarmPlaces: Object.entries(warmResult.places)
      .map(([place, quantity]) => `${roundDecimal(quantity)} ${place}`)
      .join(' / '),
  };
}
